import { nanoid } from '@reduxjs/toolkit';

const changeArrayItemIndex = <T>(
  arr: T[],
  fromIndex: number,
  toIndex: number,
): T[] => {
  const copyArr = [...arr];
  const startIndex = fromIndex < 0 ? copyArr.length + fromIndex : fromIndex;

  if (startIndex >= 0 && startIndex < copyArr.length) {
    const endIndex = toIndex < 0 ? copyArr.length + toIndex : toIndex;
    const [item] = copyArr.splice(fromIndex, 1);

    copyArr.splice(endIndex, 0, item);
  }

  return copyArr;
};

const isEmptyObject = <T extends object>(obj: T | null | undefined): boolean => {
  if (obj === null || obj === undefined) return true;
  return Object.values(obj).every(value => value === null || value === undefined);
};

const convertArraysToOneLength = <T extends object>(
  oldData: T[] | undefined,
  newData: T[],
  correctDefaultValue: T,
): { oldData: T[]; newData: T[] } => {
  if (!oldData || oldData.length === 0) {
    return { oldData: [correctDefaultValue], newData };
  }

  if (oldData.every(isEmptyObject)) {
    return { oldData: [correctDefaultValue], newData };
  }

  if (!newData || newData.length === 0) {
    return { newData: [correctDefaultValue], oldData };
  }

  if (newData.every(isEmptyObject)) {
    return { newData: [correctDefaultValue], oldData };
  }

  const maxLength = Math.max(oldData.length, newData.length);
  const newOldData = [...oldData, ...Array(maxLength - oldData.length).fill(correctDefaultValue)];
  const newNewData = [...newData, ...Array(maxLength - newData.length).fill(correctDefaultValue)];

  return { oldData: newOldData, newData: newNewData };
};

const addIdsToValues = (data: any[]) => {
  return data.map((i) => ({ ...i, id: nanoid() }));
};

export { convertArraysToOneLength, changeArrayItemIndex, addIdsToValues };
